/**
 * IAN Assist — Email Reader Service
 * Reads email content from Outlook via Office.js APIs.
 * Extracted from emailProcessor.js in Streamline-assist.
 */

/* global Office */

const MAX_CONTENT_LENGTH = 10000;

/**
 * Read the current email — works in both read and compose mode.
 * @returns {Promise<Object>} Email data shaped for RequestV1
 */
export async function readCurrentEmail() {
  const item = Office.context.mailbox.item;
  if (!item) {
    throw new Error('No email item available');
  }

  const mode = detectEmailMode(item);
  const [bodyText, bodyHtml] = await Promise.all([
    getBody(item, Office.CoercionType.Text),
    getBody(item, Office.CoercionType.Html),
  ]);

  const email = {
    id: item.itemId || '',
    conversationId: item.conversationId || '',
    subject: typeof item.subject === 'string' ? item.subject : '',
    from: getSender(item),
    to: getRecipients(item.to),
    cc: getRecipients(item.cc),
    receivedAt: item.dateTimeCreated ? item.dateTimeCreated.toISOString() : new Date().toISOString(),
    bodyText: cleanContent(bodyText),
    bodyHtml: bodyHtml,
    thread: [],
    attachments: getAttachments(item),
    mode,
  };

  return email;
}

/**
 * Detect read vs compose mode.
 */
export function detectEmailMode(item) {
  try {
    if (!item) return 'read';
    if (!item.dateTimeCreated) return 'compose';
    if (item.isDraft) return 'compose';
    return 'read';
  } catch {
    return 'read';
  }
}

/**
 * Get body content with a specific coercion type.
 */
function getBody(item, coercionType) {
  return new Promise((resolve, reject) => {
    item.body.getAsync(coercionType, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value);
      } else {
        reject(new Error(result.error?.message || 'Failed to read email body'));
      }
    });
  });
}

/**
 * Get sender info (read mode).
 */
function getSender(item) {
  try {
    const sender = item.sender || item.from;
    return {
      name: sender?.displayName || '',
      email: sender?.emailAddress || '',
    };
  } catch {
    return { name: '', email: '' };
  }
}

/**
 * Get recipients array.
 */
function getRecipients(recipientList) {
  try {
    if (!recipientList || !Array.isArray(recipientList)) return [];
    return recipientList.map((r) => ({
      name: r.displayName || r.name || '',
      email: r.emailAddress || r.address || '',
    }));
  } catch {
    return [];
  }
}

/**
 * Get attachment metadata (no content — just names/sizes).
 */
function getAttachments(item) {
  try {
    if (!item.attachments || !item.attachments.length) return [];
    return item.attachments.map((a) => ({
      name: a.name || '',
      size: a.size || 0,
      type: a.contentType || '',
    }));
  } catch {
    return [];
  }
}

/**
 * Clean and truncate content for safe transmission.
 */
function cleanContent(content) {
  if (!content) return '';
  return content.replace(/\r\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim().substring(0, MAX_CONTENT_LENGTH);
}

/**
 * Insert HTML content into the compose body.
 */
export async function insertIntoBody(htmlContent, replaceAll = true) {
  const item = Office.context.mailbox.item;
  return new Promise((resolve, reject) => {
    const method = replaceAll ? 'setAsync' : 'setSelectedDataAsync';
    item.body[method](htmlContent, { coercionType: Office.CoercionType.Html }, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(true);
      } else {
        reject(new Error(result.error?.message || 'Failed to insert content'));
      }
    });
  });
}

/**
 * Get user-selected text (compose mode only).
 */
export async function getSelectedText() {
  const item = Office.context.mailbox.item;
  return new Promise((resolve, reject) => {
    item.body.getSelectedDataAsync(Office.CoercionType.Text, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        resolve(result.value?.data || '');
      } else {
        reject(new Error(result.error?.message || 'Failed to get selected text'));
      }
    });
  });
}
